import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import { useRef, useState, useEffect, useCallback } from "react";
import MemoryBookReassignModal from "./MemoryBookAssignModal";
import { MemoryBookEntry } from "./MemoryBookCard";
import MemoryBookMinimisedPill from "./MemoryBookMinimisedPill";
import StatusColumn from "./StatusColumn";
import {
  fetchMemoryBookWidget,
  mapWidgetEntryToMemoryBookEntry,
} from "../service/memoryBookWidget.api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import apiService from "../../../service/Api";
import { getErrorMessage } from "../../../utils/getErrorMessage";

type MemoryBookStatus = "overdue" | "in_danger" | "safe";
type MobileTab = "summary" | MemoryBookStatus;

interface StaffAssignment {
  id: number;
  avatarUrl?: string;
  name: string;
  assignedCount: number;
}

interface MemoryBookSummaryProps {
  expanded: boolean;
  onMinimise: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

const MOBILE_TABS: { key: MobileTab; label: string; color: string }[] = [
  { key: "summary", label: "Summary", color: "#94a3b8" },
  { key: "overdue", label: "Overdue", color: "#E11D48" },
  { key: "in_danger", label: "In Danger", color: "#f59e0b" },
  { key: "safe", label: "Safe", color: "#10b981" },
];

const MemoryBookSummary = ({
  expanded,
  onMinimise,
  onNext,
}: MemoryBookSummaryProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [mobileTab, setMobileTab] = useState<MobileTab>("summary");
  const [modalEntry, setModalEntry] = useState<MemoryBookEntry | null>(null);
  const [entries, setEntries] = useState<MemoryBookEntry[]>([]);
  const [staffAssignments, setStaffAssignments] = useState<StaffAssignment[]>(
    [],
  );
  const [entryCounts, setEntryCounts] = useState({
    overdue: 0,
    in_danger: 0,
    safe: 0,
  });
  const [loading, setLoading] = useState(true);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMemoryBookWidget();
      const data = res.data;
      setEntries((data.entries ?? []).map(mapWidgetEntryToMemoryBookEntry));
      setStaffAssignments(
        (data.staff ?? []).map((s) => ({
          id: s.id,
          name: s.name,
          avatarUrl: s.avatar_url ?? undefined,
          assignedCount: s.assigned_count,
        })),
      );
      setEntryCounts(
        data.counts?.entries ?? { overdue: 0, in_danger: 0, safe: 0 },
      );
    } catch (err) {
      console.error("Failed to fetch memory book widget:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const handleAssignStaff = async (streamId: number, staffId: number) => {
    try {
      await apiService.post(`/api/streams/memory-book-assignment/`, {
        stream_id: streamId,
        assigned_to_ids: [staffId],
      });
      toast.success("Assignee added!");
      loadAssignments();
    } catch (err) {
      const message = getErrorMessage(err, "Failed to assign staff member.");
      toast.error(message);
    }
  };

  const handleChangeRole = async (
    assignmentId: number,
    role: "scanner" | "uploader" | "both" | "other" | null,
  ) => {
    try {
      await apiService.patch(
        `/api/streams/memory-book-assignment/${assignmentId}/`,
        { role },
      );
      toast.success("Role updated!");
      loadAssignments();
    } catch {
      toast.error("Failed to update role.");
    }
  };

  const handleUnassignStaff = async (assignmentId: number) => {
    try {
      await apiService.delete(
        `/api/streams/memory-book-assignment/${assignmentId}/`,
      );
      toast.success("Assignee removed!");
      loadAssignments();
    } catch {
      toast.error("Failed to unassign staff member.");
    }
  };

  const computedCounts = {
    overdue: entryCounts.overdue,
    inDanger: entryCounts.in_danger,
    safe: entryCounts.safe,
  };

  const grouped = {
    overdue: entries.filter((e) => e.status === "overdue"),
    in_danger: entries.filter((e) => e.status === "in_danger"),
    safe: entries.filter((e) => e.status === "safe"),
  };

  if (!expanded) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "flex-end", alignSelf: "stretch" }}
      >
        <MemoryBookMinimisedPill
          overdueCount={entryCounts.overdue}
          inDangerCount={entryCounts.in_danger}
          onExpand={onMinimise}
        />
      </Box>
    );
  }

  // ─── Shared top nav bar ───────────────────────────────────────────────────
  const TopNav = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        mb: 2,
      }}
    >
      <IconButton
        sx={{
          backgroundColor: colors.primary[800],
          color: colors.primary[100],
          width: 32,
          height: 32,
          border: `1px solid ${colors.primary[300]}30`,
        }}
      >
        <ChevronLeft sx={{ fontSize: 18 }} />
      </IconButton>

      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.9rem",
          color: colors.primary[100],
          minWidth: { xs: 140, sm: 180 },
          textAlign: "center",
        }}
      >
        Memory Book Summary
      </Typography>

      <IconButton
        onClick={() => onNext?.()}
        sx={{
          backgroundColor: colors.primary[400],
          color: "white",
          width: 32,
          height: 32,
          "&:hover": { backgroundColor: colors.primary[300] },
        }}
      >
        <ChevronRight sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  // ─── Mobile layout ────────────────────────────────────────────────────────
  if (isMobile) {
    const activeTabConfig = MOBILE_TABS.find((t) => t.key === mobileTab)!;
    const tabCountMap: Record<MobileTab, number> = {
      summary: entries.length,
      overdue: grouped.overdue.length,
      in_danger: grouped.in_danger.length,
      safe: grouped.safe.length,
    };

    return (
      <Box sx={{ width: "100%", px: 1 }}>
        <Box
          sx={{
            border: `1px solid ${colors.primary[300]}30`,
            borderRadius: "24px",
            p: 2,
            backgroundColor: colors.primary[900],
            position: "relative",
          }}
        >
          {/* Minimise */}
          <Tooltip title="Minimise" arrow>
            <IconButton
              onClick={onMinimise}
              size="small"
              sx={{
                position: "absolute",
                top: 10,
                right: 12,
                color: colors.primary[700],
                zIndex: 3,
                "&:hover": { color: colors.primary[400] },
              }}
            >
              <KeyboardArrowDown sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {TopNav}

          {/* Tab strip */}
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              mb: 2,
              backgroundColor: colors.primary[800],
              borderRadius: "14px",
              p: 0.5,
            }}
          >
            {MOBILE_TABS.map((tab) => {
              const isActive = mobileTab === tab.key;
              const count = tabCountMap[tab.key];
              return (
                <Box
                  key={tab.key}
                  onClick={() => setMobileTab(tab.key)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.3,
                    py: 1,
                    px: 0.5,
                    borderRadius: "10px",
                    cursor: "pointer",
                    backgroundColor: isActive
                      ? colors.primary[900]
                      : "transparent",
                    transition: "background-color 0.2s ease",
                    borderBottom: isActive
                      ? `2px solid ${tab.color}`
                      : "2px solid transparent",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive
                        ? colors.primary[400]
                        : colors.primary[700],
                      lineHeight: 1,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab.label}
                  </Typography>
                  {tab.key !== "summary" && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: isActive ? tab.color : colors.primary[700],
                        lineHeight: 1,
                      }}
                    >
                      {count}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Tab content */}
          {mobileTab === "summary" ? (
            // Summary tab
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Status counts */}
              <Box
                sx={{
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.1em",
                    color: colors.primary[700],
                    mb: 0.5,
                  }}
                >
                  STATUS OVERVIEW
                </Typography>
                {[
                  {
                    label: "Over Due",
                    count: computedCounts.overdue,
                    color: "#E11D48",
                    tab: "overdue" as MobileTab,
                  },
                  {
                    label: "In Danger",
                    count: computedCounts.inDanger,
                    color: "#f59e0b",
                    tab: "in_danger" as MobileTab,
                  },
                  {
                    label: "Safe",
                    count: computedCounts.safe,
                    color: "#10b981",
                    tab: "safe" as MobileTab,
                  },
                ].map(({ label, count, color, tab }) => (
                  <Box
                    key={label}
                    onClick={() => setMobileTab(tab)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      py: 0.5,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.85rem", color: colors.primary[100] }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={count}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          backgroundColor: color + "20",
                          color: color,
                          "& .MuiChip-label": { px: 1.2 },
                        }}
                      />
                      <ChevronRight
                        sx={{ fontSize: 14, color: colors.primary[600] }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Staff assignments */}
              <Box
                sx={{
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.1em",
                    color: colors.primary[700],
                    mb: 1.5,
                  }}
                >
                  ASSIGNMENTS
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {staffAssignments.map((staff) => (
                    <Tooltip
                      key={staff.id}
                      title={`${staff.name}: ${staff.assignedCount} assigned`}
                      arrow
                    >
                      <Box sx={{ position: "relative", cursor: "pointer" }}>
                        <Avatar
                          src={staff.avatarUrl}
                          sx={{
                            width: 44,
                            height: 44,
                            border: `2px solid ${colors.primary[400]}`,
                          }}
                        >
                          {staff.name[0]}
                        </Avatar>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: -3,
                            right: -3,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            backgroundColor: "#E11D48",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: `1.5px solid ${colors.primary[900]}`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "white",
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {staff.assignedCount}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: "60vh",
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                pb: 1,
              }}
            >
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      height: 64,
                      borderRadius: "16px",
                      backgroundColor: colors.primary[800],
                      opacity: 0.5 + i * 0.15,
                    }}
                  />
                ))
              ) : (grouped[mobileTab as MemoryBookStatus] ?? []).length ===
                0 ? (
                <Box
                  sx={{
                    py: 6,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: activeTabConfig.color + "20",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: activeTabConfig.color,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.8rem", color: colors.primary[600] }}
                  >
                    No {activeTabConfig.label.toLowerCase()} entries
                  </Typography>
                </Box>
              ) : (
                (grouped[mobileTab as MemoryBookStatus] ?? []).map((entry) => (
                  <Box
                    key={entry.id}
                    onClick={() => setModalEntry(entry)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "16px",
                      backgroundColor: colors.primary[800],
                      cursor: "pointer",
                      borderLeft: `3px solid ${activeTabConfig.color}`,
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        backgroundColor: colors.primary[700] + "60",
                      },
                    }}
                  >
                    <Avatar
                      src={entry.deceasedAvatarUrl}
                      sx={{ width: 40, height: 40, flexShrink: 0 }}
                    >
                      {entry.deceasedName[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: colors.primary[100],
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {entry.deceasedName}
                      </Typography>
                      <Box
                        sx={{
                          height: 3,
                          borderRadius: 2,
                          backgroundColor: colors.primary[300] + "30",
                          my: 0.5,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${entry.progressPercent}%`,
                            borderRadius: 2,
                            backgroundColor: activeTabConfig.color,
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: colors.primary[700],
                          }}
                        >
                          {entry.timeInfo}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: colors.primary[700],
                          }}
                        >
                          {entry.commentCount} comments
                        </Typography>
                      </Box>
                    </Box>
                    <ChevronRight
                      sx={{
                        fontSize: 16,
                        color: colors.primary[700],
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>

        <MemoryBookReassignModal
          open={!!modalEntry}
          onClose={() => setModalEntry(null)}
          entry={modalEntry}
          staffOptions={staffAssignments}
          onAssign={handleAssignStaff}
          onUnassign={handleUnassignStaff}
          onChangeRole={handleChangeRole}
          onViewBook={() => {
            if (modalEntry) {
              navigate(`/memory-table/view/${modalEntry.streamId}`);
              setModalEntry(null);
            }
          }}
        />
      </Box>
    );
  }

  // Desktop
  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          width: "fit-content",
          mx: "auto",
          maxWidth: { xs: "calc(100vw - 32px)", md: "fit-content" },
        }}
      >
        <Box
          sx={{
            border: `1px solid ${colors.primary[300]}30`,
            borderRadius: "40px",
            p: { xs: 1.5, md: 3 },
            boxShadow: "0 6px 28px rgba(2, 2, 2, 0.06)",
            backgroundColor: colors.primary[900],
            position: "relative",
          }}
        >
          {/* Minimise button */}
          <Tooltip title="Minimise" arrow>
            <IconButton
              onClick={onMinimise}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 16,
                color: colors.primary[700],
                zIndex: 3,
                "&:hover": { color: colors.primary[400] },
              }}
            >
              <KeyboardArrowDown sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {TopNav}

          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              sx={{
                minWidth: { xs: 200, sm: 240, md: 260 },
                maxWidth: { xs: 200, sm: 240, md: 260 },
                backgroundColor: colors.primary[800],
                borderRadius: "30px",
                p: 2.5,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  letterSpacing: "0.1em",
                  color: colors.primary[700],
                }}
              >
                MEMORY BOOKS SUMMARY
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {[
                  {
                    label: "Over Due",
                    count: computedCounts.overdue,
                    color: "#E11D48",
                  },
                  {
                    label: "In Danger",
                    count: computedCounts.inDanger,
                    color: "#f59e0b",
                  },
                  {
                    label: "SAFE",
                    count: computedCounts.safe,
                    color: "#10b981",
                  },
                ].map(({ label, count, color }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.8rem", color: colors.primary[100] }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: colors.primary[700] + "30",
                        color: colors.primary[100],
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Staff assignments */}
              <Box
                sx={{
                  borderRadius: "20px",
                  backgroundColor: colors.primary[900],
                  px: 1.5,
                  py: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: colors.primary[700],
                    mb: 1.5,
                  }}
                >
                  Memory book assignments
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: 24,
                      pointerEvents: "none",
                      background: `linear-gradient(to right, transparent, ${colors.primary[900]})`,
                      opacity: staffAssignments.length > 5 ? 1 : 0,
                      transition: "opacity 0.2s ease",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      pb: 0.5,
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    {staffAssignments.map((staff) => (
                      <Tooltip
                        key={staff.id}
                        title={`${staff.name}: ${staff.assignedCount} assigned`}
                        arrow
                      >
                        <Box
                          sx={{
                            position: "relative",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        >
                          <Avatar
                            src={staff.avatarUrl}
                            sx={{
                              width: 40,
                              height: 40,
                              border: `2px solid ${colors.primary[400]}`,
                            }}
                          >
                            {staff.name[0]}
                          </Avatar>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: -4,
                              right: -4,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: "#E11D48",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1.5px solid ${colors.primary[900]}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "white",
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {staff.assignedCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            {(["overdue", "in_danger", "safe"] as MemoryBookStatus[]).map(
              (status) => (
                <StatusColumn
                  key={status}
                  status={status}
                  entries={loading ? [] : grouped[status]}
                  colors={colors}
                  loading={loading}
                  onCardClick={(entry) => setModalEntry(entry)}
                />
              ),
            )}
          </Box>
        </Box>
      </Box>

      <MemoryBookReassignModal
        open={!!modalEntry}
        onClose={() => setModalEntry(null)}
        entry={modalEntry}
        staffOptions={staffAssignments}
        onAssign={handleAssignStaff}
        onUnassign={handleUnassignStaff}
        onChangeRole={handleChangeRole}
        onViewBook={() => {
          if (modalEntry) {
            navigate(`/memory-table/view/${modalEntry.streamId}`);
            setModalEntry(null);
          }
        }}
      />
    </Box>
  );
};

export default MemoryBookSummary;
